import { useCallback, useEffect, useMemo, useState } from 'react'
import Sheet from './Sheet.jsx'
import {
  UNITS, clearGoal, deleteLog, getGoal, getLogs, logWeight, progress, setGoal, smooth,
} from '../lib/weight.js'
import { shortDate, todayISO } from '../lib/format.js'

const n1 = (v) => (v == null ? '—' : Number(v).toFixed(1))

/**
 * The raw line thin and pale, the seven-day average solid on top. The average
 * is the thing worth reading; the daily points are mostly water and salt, and
 * drawing them as if each one meant something is how these screens end up
 * making people feel worse.
 */
function Chart({ points, goal }) {
  if (points.length < 2) return null
  const W = 320
  const H = 110
  const PAD = 14

  const vals = points.flatMap((p) => [Number(p.weight), p.avg])
  if (goal?.target_weight != null) vals.push(Number(goal.target_weight))
  const lo = Math.min(...vals)
  const hi = Math.max(...vals)
  const span = hi - lo || 1

  const t0 = new Date(points[0].logged_on).getTime()
  const t1 = new Date(points[points.length - 1].logged_on).getTime()
  const dt = t1 - t0 || 1
  const x = (p) => PAD + ((new Date(p.logged_on).getTime() - t0) / dt) * (W - PAD * 2)
  const y = (v) => H - PAD - ((v - lo) / span) * (H - PAD * 2)

  const line = (get) =>
    points.map((p, i) => (i ? 'L' : 'M') + x(p).toFixed(1) + ' ' + y(get(p)).toFixed(1)).join(' ')

  const gy = goal?.target_weight != null ? y(Number(goal.target_weight)) : null

  return (
    <svg className="chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Weight over time">
      {gy != null ? (
        <>
          <line x1={PAD} y1={gy} x2={W - PAD} y2={gy}
            stroke="var(--health)" strokeWidth="1" strokeDasharray="3 3" />
          <text x={W - PAD} y={gy - 4} textAnchor="end" className="clabel">
            goal {n1(goal.target_weight)}
          </text>
        </>
      ) : null}

      <path d={line((p) => Number(p.weight))} fill="none"
        stroke="var(--ink-faint)" strokeWidth="1" opacity="0.55"
        vectorEffect="non-scaling-stroke" />
      <path d={line((p) => p.avg)} fill="none"
        stroke="var(--ocean)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round"
        vectorEffect="non-scaling-stroke" />
      <circle cx={x(points[points.length - 1])} cy={y(points[points.length - 1].avg)}
        r="3" fill="var(--ocean)" />
    </svg>
  )
}

export default function Weight({ toast }) {
  const [logs, setLogs] = useState(null)
  const [goal, setGoalRow] = useState(null)
  const [error, setError] = useState(null)
  const [sheet, setSheet] = useState(null)
  const [showAll, setShowAll] = useState(false)

  const load = useCallback(async () => {
    setError(null)
    try {
      const [l, g] = await Promise.all([getLogs(), getGoal()])
      setLogs(l)
      setGoalRow(g)
    } catch (e) {
      setError(e.message || String(e))
    }
  }, [])

  useEffect(() => { load() }, [load])

  const points = useMemo(() => (logs ? smooth(logs) : []), [logs])
  const p = useMemo(() => (logs ? progress(logs, goal) : null), [logs, goal])
  const unit = goal?.unit || 'kg'

  if (error) {
    return (
      <div className="notice warn">
        <p style={{ margin: 0, fontSize: 14 }}>Could not read your weight log. {error}</p>
      </div>
    )
  }
  if (!logs) return null

  const recent = showAll ? [...logs].reverse() : [...logs].reverse().slice(0, 7)

  return (
    <>
      <p className="lbl" style={{ margin: '24px 2px 8px' }}>Weight</p>

      <div className="card">
        <div className="pad">
          {p ? (
            <>
              <div className="in-total">
                <span className="num serif">{n1(p.now)}<em className="wu">{unit}</em></span>
                <span className="lbl">on {shortDate(p.on)}</span>
              </div>
              <div className="wgrid">
                <div>
                  <span className="lbl">7-day average</span>
                  <b className="num">{n1(p.avgNow)}</b>
                </div>
                <div>
                  <span className="lbl">Since start</span>
                  <b className="num">
                    {p.changed > 0 ? '+' : p.changed < 0 ? '−' : ''}
                    {n1(Math.abs(p.changed))}
                  </b>
                </div>
                {goal ? (
                  <div>
                    <span className="lbl">To goal</span>
                    <b className="num">
                      {p.toGo <= 0 ? 'reached' : n1(p.toGo)}
                    </b>
                  </div>
                ) : null}
                <div>
                  <span className="lbl">Per week</span>
                  <b className="num">
                    {p.perWeek == null
                      ? 'not enough yet'
                      : (p.perWeek > 0 ? '+' : p.perWeek < 0 ? '−' : '') + n1(Math.abs(p.perWeek))}
                  </b>
                </div>
              </div>

              {goal && p.pct != null ? (
                <>
                  <div className="rail" style={{ margin: '12px 0 5px' }}>
                    <i style={{ width: p.pct + '%', background: 'var(--health)' }} />
                  </div>
                  <p className="lbl" style={{ margin: 0 }}>
                    {p.pct}% of the way
                    {goal.target_on ? ' · target date ' + shortDate(goal.target_on) : ''}
                  </p>
                </>
              ) : null}
            </>
          ) : (
            <p style={{ margin: 0, fontSize: 14, color: 'var(--ink-dim)' }}>
              Nothing logged yet. Add today's weight and the trend builds from there.
            </p>
          )}
        </div>

        {points.length > 1 ? (
          <div className="chart-wrap">
            <Chart points={points} goal={goal} />
            <p className="lbl chart-note">
              Pale line is each entry. Solid is the seven-day average — that is the one that
              means something.
            </p>
          </div>
        ) : null}
      </div>

      {recent.length ? (
        <div className="card" style={{ marginTop: 12 }}>
          <div className="rows">
            {recent.map((l) => (
              <button className="row" key={l.id} type="button"
                onClick={() => setSheet({ kind: 'log', entry: l })}>
                <div className="nm">
                  <b style={{ fontWeight: 400 }}>{shortDate(l.logged_on)}</b>
                  {l.note ? <span className="lbl">{l.note}</span> : null}
                </div>
                <span className="amt num">{n1(l.weight)}<em className="wu">{unit}</em></span>
                <span className="go" aria-hidden="true">›</span>
              </button>
            ))}
          </div>
          {logs.length > 7 ? (
            <div className="build-acts">
              <button type="button" onClick={() => setShowAll((v) => !v)}>
                {showAll ? 'Show less' : 'All ' + logs.length}
              </button>
              <span />
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="sheet-actions" style={{ marginTop: 12 }}>
        <button type="button" className="btn" onClick={() => setSheet({ kind: 'log' })}>
          Log weight
        </button>
        <button type="button" className="btn ghost" onClick={() => setSheet({ kind: 'goal' })}>
          {goal ? 'Change goal' : 'Set a goal'}
        </button>
      </div>

      {sheet?.kind === 'log' ? (
        <LogSheet
          entry={sheet.entry}
          unit={unit}
          onClose={() => setSheet(null)}
          onSaved={() => { setSheet(null); load() }}
          toast={toast}
        />
      ) : null}

      {sheet?.kind === 'goal' ? (
        <GoalSheet
          goal={goal}
          latest={p?.now}
          onClose={() => setSheet(null)}
          onSaved={() => { setSheet(null); load() }}
          toast={toast}
        />
      ) : null}
    </>
  )
}

function LogSheet({ entry, unit, onClose, onSaved, toast }) {
  const [w, setW] = useState(entry ? String(entry.weight) : '')
  const [on, setOn] = useState(entry?.logged_on ?? todayISO())
  const [note, setNote] = useState(entry?.note ?? '')
  const [busy, setBusy] = useState(false)

  const value = parseFloat(w)
  const valid = Number.isFinite(value) && value > 0 && value < 1000

  return (
    <Sheet title={entry ? 'Edit entry' : 'Log weight'} onClose={onClose}>
      <form
        onSubmit={async (e) => {
          e.preventDefault()
          if (busy || !valid) return
          setBusy(true)
          try {
            await logWeight({ weight: value, on, note })
            onSaved()
          } catch (err) {
            toast?.(err.message || 'Could not save that.')
          } finally {
            setBusy(false)
          }
        }}
      >
        <div className="field">
          <label className="lbl" htmlFor="w-val">Weight in {unit}</label>
          <input id="w-val" inputMode="decimal" required placeholder="0.0"
            value={w} onChange={(e) => setW(e.target.value)} />
        </div>
        <div className="field">
          <label className="lbl" htmlFor="w-on">Date</label>
          <input id="w-on" type="date" required max={todayISO()}
            value={on} onChange={(e) => setOn(e.target.value)} />
          <p className="lbl" style={{ margin: '6px 0 0' }}>
            One entry a day. Logging the same date again replaces it.
          </p>
        </div>
        <div className="field">
          <label className="lbl" htmlFor="w-note">Note</label>
          <input id="w-note" placeholder="Optional"
            value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
        <button className="btn" type="submit" data-sheet-primary={!entry || undefined} disabled={busy || !valid}>
          {busy ? 'Saving…' : 'Save'}
        </button>

        {entry ? (
          <button type="button" className="btn ghost" style={{ marginTop: 10 }}
            onClick={async () => {
              try {
                await deleteLog(entry.id)
                onSaved()
              } catch (err) {
                toast?.(err.message || 'Could not remove that.')
              }
            }}>
            Remove this entry
          </button>
        ) : null}
      </form>
    </Sheet>
  )
}

function GoalSheet({ goal, latest, onClose, onSaved, toast }) {
  const [unit, setUnit] = useState(goal?.unit ?? 'kg')
  const [target, setTarget] = useState(goal ? String(goal.target_weight) : '')
  const [by, setBy] = useState(goal?.target_on ?? '')
  const [busy, setBusy] = useState(false)

  const t = parseFloat(target)
  const valid = Number.isFinite(t) && t > 0 && t < 1000

  return (
    <Sheet title={goal ? 'Change the goal' : 'Set a goal'} onClose={onClose}>
      <form
        onSubmit={async (e) => {
          e.preventDefault()
          if (busy || !valid) return
          setBusy(true)
          try {
            /* A new goal row rather than an edit, so the old one stays as
               history instead of being quietly rewritten. */
            if (goal) await clearGoal(goal.id)
            await setGoal({
              unit,
              targetWeight: t,
              startWeight: latest ?? null,
              targetOn: by || null,
            })
            onSaved()
          } catch (err) {
            toast?.(err.message || 'Could not save that.')
          } finally {
            setBusy(false)
          }
        }}
      >
        <div className="field">
          <label className="lbl" htmlFor="g-unit">Units</label>
          <select id="g-unit" value={unit} onChange={(e) => setUnit(e.target.value)}>
            {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>
        <div className="field">
          <label className="lbl" htmlFor="g-target">Target weight</label>
          <input id="g-target" inputMode="decimal" required placeholder="0.0"
            value={target} onChange={(e) => setTarget(e.target.value)} />
        </div>
        <div className="field">
          <label className="lbl" htmlFor="g-by">By when</label>
          <input id="g-by" type="date" value={by} onChange={(e) => setBy(e.target.value)} />
          <p className="lbl" style={{ margin: '6px 0 0' }}>
            Optional. A date here sets a marker, not a deadline the app will nag you about.
          </p>
        </div>

        <button className="btn" type="submit" data-sheet-primary={!goal || undefined} disabled={busy || !valid}>
          {busy ? 'Saving…' : 'Save goal'}
        </button>

        {goal ? (
          <button type="button" className="btn ghost" style={{ marginTop: 10 }}
            onClick={async () => {
              try {
                await clearGoal(goal.id)
                onSaved()
              } catch (err) {
                toast?.(err.message || 'Could not clear that.')
              }
            }}>
            Clear the goal, keep the log
          </button>
        ) : null}
      </form>
    </Sheet>
  )
}
